import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, ArrowLeft, Crown, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { submitPendingQuiz } from "@/lib/pendingQuiz";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grid-paper">
      {/* brutalist brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-brand-ink text-brand-paper p-10 border-r border-border overflow-hidden">
        <Link to="/" data-testid="auth-logo" className="flex items-center gap-2.5 relative z-10">
          <span className="grid place-items-center h-9 w-9 bg-brand-lime border-2 border-brand-paper">
            <Zap className="h-5 w-5 text-foreground fill-foreground" />
          </span>
          <span className="font-heading font-extrabold text-lg">AI Pathshaalaa</span>
        </Link>

        <div className="absolute -right-16 top-1/4 h-72 w-72 bg-brand-pink/30 rotate-45" />
        <div className="absolute -left-10 bottom-1/4 h-52 w-52 bg-brand-cyber/30" />

        <div className="relative z-10">
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-brand-tangerine">Learn or get left behind</p>
          <h2 className="mt-3 font-heading font-extrabold tracking-tight text-4xl leading-[0.95] max-w-md">
            One clear path from <span className="text-brand-lime">zero to AI mastery.</span>
          </h2>
          <div className="mt-6 inline-flex items-start gap-2 bg-card text-foreground border-2 border-brand-paper p-3 max-w-sm">
            <Crown className="h-5 w-5 text-brand-cyber shrink-0 mt-0.5" />
            <p className="text-sm font-medium">We curate the best free teachers on YouTube — judged on teaching, not followers.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-10">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground mb-8 lg:hidden">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <h1 className="font-heading font-extrabold tracking-tight text-3xl">{title}</h1>
            <p className="mt-2 text-muted-foreground font-medium">{subtitle}</p>
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function GoogleButton({ label }) {
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" data-testid="google-login" onClick={googleLogin} className="w-full h-11 gap-3">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
      {label}
    </Button>
  );
}

export default function Login() {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const pending = await submitPendingQuiz();
      if (pending) {
        await checkAuth();
        navigate((user.role === "admin" || user.is_subscribed) ? "/dashboard" : "/subscribe", { replace: true });
        return;
      }
      const dest = location.state?.from?.pathname;
      navigate(
        dest || (user.role === "admin" ? "/admin" : user.has_path ? "/dashboard" : "/quiz"),
        { replace: true }
      );
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your AI path.">
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-10 h-11 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        </div>
        {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 bg-primary text-primary-foreground">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton label="Continue with Google" />

      <p className="mt-6 text-sm text-muted-foreground text-center">
        New here?{" "}
        <Link to="/register" data-testid="to-register" className="text-primary font-medium hover:underline">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export { GoogleButton };
